import React, { useState, createElement } from 'react';
import { motion } from 'framer-motion';
import { Share2, MessageCircle, Star, Link as LinkIcon, Heart, Users, Copy, Check, Quote } from 'lucide-react';
import { PageTransition } from '../components/PageTransition';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
export function SharePage() {
  const [copied, setCopied] = useState(false);
  const podcastLink = 'https://open.spotify.com/show/5nWVRvG1GtlfUXUfN77LfL';
  const handleCopyLink = async () => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(podcastLink);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = podcastLink;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };
  return <PageTransition data-id="element-675">
      <div className="pt-24 pb-16 min-h-screen" data-id="element-676">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-677">
          {/* Hero Section */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-center mb-20" data-id="element-678">
            <div className="w-16 h-16 bg-gold-500/10 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-679">
              <Share2 className="w-8 h-8 text-gold-500" data-id="element-680" />
            </div>
            <h1 className="text-4xl md:text-6xl font-serif font-bold text-white mb-6" data-id="element-681">
              Share the Mission
            </h1>
            <div className="w-24 h-1 bg-gold-500 mx-auto rounded-full mb-6" data-id="element-682"></div>
            <p className="text-xl text-white/80 max-w-2xl mx-auto leading-relaxed" data-id="element-683">
              Help us spread the word. Every share, review, and conversation
              helps us reach more people with honest stories of faith and
              transformation.
            </p>
          </motion.div>

          {/* Ways to Share Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-24" data-id="element-684">
            {/* Social Media */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.1
          }} data-id="element-685">
              <Card className="h-full bg-dark-600 border-dark-500 p-8 text-center hover:border-gold-500/50 transition-colors group" data-id="element-686">
                <div className="w-14 h-14 bg-dark-500 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform" data-id="element-687">
                  <MessageCircle className="w-7 h-7 text-gold-400" data-id="element-688" />
                </div>
                <h3 className="text-xl font-serif font-bold text-white mb-4" data-id="element-689">
                  Share on Social
                </h3>
                <p className="text-white/70 mb-8 leading-relaxed" data-id="element-690">
                  Post your favorite episode on Facebook, Instagram, or X. Tag
                  us and let your friends know how the story impacted you.
                </p>
                <div className="flex flex-col gap-3 mt-auto" data-id="element-691">
                  <a href="https://www.facebook.com/sharer/sharer.php?u=https://open.spotify.com/show/5nWVRvG1GtlfUXUfN77LfL" target="_blank" rel="noopener noreferrer" data-id="element-692">
                    <Button variant="outline" className="w-full" data-id="element-693">
                      Share to Facebook
                    </Button>
                  </a>
                  <a href="https://twitter.com/intent/tweet?url=https://open.spotify.com/show/5nWVRvG1GtlfUXUfN77LfL&text=Check%20out%20the%203%20Doves%20Podcast!" target="_blank" rel="noopener noreferrer" data-id="element-694">
                    <Button variant="outline" className="w-full" data-id="element-695">
                      Share to X
                    </Button>
                  </a>
                </div>
              </Card>
            </motion.div>

            {/* Send a Link */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} data-id="element-696">
              <Card className="h-full bg-dark-600 border-dark-500 p-8 text-center hover:border-gold-500/50 transition-colors group" data-id="element-697">
                <div className="w-14 h-14 bg-dark-500 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform" data-id="element-698">
                  <LinkIcon className="w-7 h-7 text-gold-400" data-id="element-699" />
                </div>
                <h3 className="text-xl font-serif font-bold text-white mb-4" data-id="element-700">
                  Send a Link
                </h3>
                <p className="text-white/70 mb-6 leading-relaxed" data-id="element-701">
                  Know someone who needs to hear a specific message? Grab the
                  link and text it directly to a friend or family member.
                </p>
                <div className="mb-4 p-3 bg-dark-700 rounded-lg border border-dark-400 flex items-center gap-2" data-id="element-702">
                  <LinkIcon className="w-4 h-4 text-gold-500 flex-shrink-0" data-id="element-703" />
                  <span className="text-white/60 text-sm truncate select-all" data-id="element-704">
                    {podcastLink}
                  </span>
                </div>
                <div className="mt-auto flex flex-col gap-3" data-id="element-705">
                  <Button variant="primary" className="w-full" onClick={handleCopyLink} leftIcon={copied ? <Check className="w-4 h-4" data-id="element-707" /> : <Copy className="w-4 h-4" data-id="element-708" />} data-id="element-706">
                    {copied ? 'Link Copied!' : 'Copy Podcast Link'}
                  </Button>
                  <a href={podcastLink} target="_blank" rel="noopener noreferrer" data-id="element-709">
                    <Button variant="outline" className="w-full" data-id="element-710">
                      Open in Spotify
                    </Button>
                  </a>
                </div>
              </Card>
            </motion.div>

            {/* Word of Mouth */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3
          }} data-id="element-711">
              <Card className="h-full bg-dark-600 border-dark-500 p-8 text-center hover:border-gold-500/50 transition-colors group" data-id="element-712">
                <div className="w-14 h-14 bg-dark-500 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform" data-id="element-713">
                  <Users className="w-7 h-7 text-gold-400" data-id="element-714" />
                </div>
                <h3 className="text-xl font-serif font-bold text-white mb-4" data-id="element-715">
                  Tell a Friend
                </h3>
                <p className="text-white/70 mb-8 leading-relaxed" data-id="element-716">
                  The most powerful way to share is through personal
                  conversation. Mention the podcast at your small group, church,
                  or over coffee.
                </p>
                <div className="mt-auto flex items-center justify-center text-gold-500 font-medium" data-id="element-717">
                  <Heart className="w-5 h-5 mr-2" data-id="element-718" />
                  <span data-id="element-719">Share from the heart</span>
                </div>
              </Card>
            </motion.div>
          </div>

          {/* Reviews Section */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="bg-dark-800 rounded-2xl p-8 md:p-12 border border-dark-600 mb-24 relative overflow-hidden" data-id="element-720">
            <div className="absolute top-0 right-0 w-64 h-64 bg-gold-500/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3" data-id="element-721"></div>

            <div className="flex flex-col md:flex-row items-center gap-12 relative z-10" data-id="element-722">
              <div className="w-full md:w-1/2" data-id="element-723">
                <div className="flex items-center mb-6" data-id="element-724">
                  {[1, 2, 3, 4, 5].map(star => <Star key={star} className="w-6 h-6 text-gold-500 fill-gold-500 mr-1" data-id="element-725" />)}
                </div>
                <h2 className="text-3xl font-serif font-bold text-white mb-4" data-id="element-726">
                  Leave a Review
                </h2>
                <p className="text-lg text-white/80 mb-8 leading-relaxed" data-id="element-727">
                  Reviews and ratings are crucial for helping new listeners
                  discover the podcast. Taking just 60 seconds to rate and
                  review us on Apple Podcasts or Spotify makes a massive
                  difference.
                </p>
                <div className="flex flex-col sm:flex-row gap-4" data-id="element-728">
                  <a href="https://podcasts.apple.com/us/podcast/3-doves-podcast/id1871795398" target="_blank" rel="noopener noreferrer" data-id="element-729">
                    <Button className="w-full sm:w-auto" data-id="element-730">
                      Review on Apple
                    </Button>
                  </a>
                  <a href="https://open.spotify.com/show/5nWVRvG1GtlfUXUfN77LfL" target="_blank" rel="noopener noreferrer" data-id="element-731">
                    <Button variant="outline" className="w-full sm:w-auto" data-id="element-732">
                      Rate on Spotify
                    </Button>
                  </a>
                </div>
              </div>

              <div className="w-full md:w-1/2" data-id="element-733">
                <div className="bg-dark-700 p-8 rounded-xl border border-dark-500 relative" data-id="element-734">
                  <Quote className="absolute top-4 right-4 w-12 h-12 text-dark-500" data-id="element-735" />
                  <p className="text-white/90 italic text-lg leading-relaxed relative z-10" data-id="element-736">
                    "This podcast has been such an encouragement to my daily
                    walk. Hearing real, unpolished stories of how God is working
                    in everyday people reminds me that He's working in my life
                    too."
                  </p>
                  <div className="mt-6 flex items-center" data-id="element-737">
                    <div className="w-10 h-10 bg-dark-500 rounded-full flex items-center justify-center text-gold-400 font-bold" data-id="element-738">
                      L
                    </div>
                    <div className="ml-3" data-id="element-739">
                      <p className="text-white font-medium" data-id="element-740">Listener Review</p>
                      <p className="text-white/50 text-sm" data-id="element-741">Apple Podcasts</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Bottom Quote */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center max-w-3xl mx-auto" data-id="element-742">
            <h3 className="text-2xl font-serif font-bold text-gold-400 mb-4" data-id="element-743">
              "They triumphed over him by the blood of the Lamb and by the word
              of their testimony."
            </h3>
            <p className="text-white/60 uppercase tracking-widest text-sm" data-id="element-744">
              Revelation 12:11
            </p>
          </motion.div>
        </div>
      </div>
    </PageTransition>;
}